import { useMemo, useState } from "react"
import { BRAND_COLORS, type AnyScheme, type BrandColor } from "./brand"

export const DEFAULT_SETTINGS = {
  debugShapes: false,
  debugCells: false,
  showShapes: true,
  lineOffset: 0,
  scheme: "neutral" as AnyScheme,
  allowedColors: Object.keys(BRAND_COLORS) as BrandColor[],
  invertScheme: false,
  outline: true,
  lineWidth: 3,
  seed: 19482,
  warp: 0,

  advanced: false,

  rows: 12,
  cols: 12,
  density: 10,
  voids: 5,
  downsample: 0.5,
  sharpness: 1.2,

  mouseOn: false,
  autoWarpOn: false,
  renderMode: "draw" as "morph" | "draw" | "static",

  exportType: "svg" as "svg" | "png",
  exportBackground: false,
  exportWidth: 1200,
  exportPadding: 0,

  targetShape: undefined as number | undefined,
}

export type GeneratorSettings = typeof DEFAULT_SETTINGS

export function useSettingsStore() {
  const [_render, setRender] = useState(0)
  const params = useMemo(() => {
    const params: GeneratorSettings = {
      ...DEFAULT_SETTINGS,
      exportPadding: 0,
      renderMode: "static",
    }

    if (document.location.hash) {
      const hash = document.location.hash.replace("#params=", "")
      try {
        const parsedParams = JSON.parse(decodeURIComponent(hash))
        Object.assign(params, parsedParams)
      } catch (e) {
        console.error("Failed to parse params from URL", e)
      }
    }
    return params
  }, [])

  function changed() {
    setRender((r) => (r += 1))
    document.location.hash = "#params=" + encodeURIComponent(JSON.stringify(params))
  }

  return {
    get values() {
      return {
        ...params,
      }
    },
    set<K extends keyof GeneratorSettings>(key: K, value: GeneratorSettings[K]) {
      params[key] = value
      changed()
    },
    get<K extends keyof GeneratorSettings>(key: K): GeneratorSettings[K] {
      return params[key]
    },
  }
}

export type GeneratorSettingsStore = ReturnType<typeof useSettingsStore>
